import axios from "../axios/axios";
import {
  OPTIONAL_REPORT_SECTIONS,
  OnlineConsultReportPayload,
  OnlineConsultReportResponse,
} from "../types/api";

// ITAM-2598 online-consult report service.
//
// Both endpoints go through the shared, pre-configured axios instance (bearer +
// baseURL). The Transactions page is reachable only by PRODAJNO_MESTO_USER,
// which is exactly the authority these endpoints require — so a 401/403 here is
// a genuinely dead session and the global logout interceptor is the desired
// behavior (unlike the expected-403 pre-checks in consultOrderService).
//
// Meaningful error statuses pass through to the caller untouched:
//  - POST 400: consult not COMPLETED / report already exists (body is a raw
//    English string; callers show a Serbian per-status message instead),
//  - 404: consult unknown, or no report yet on the PDF endpoint.

/**
 * POST the pharmacist's report for a COMPLETED consult. On 201 the backend has
 * stored the report, generated the PDF, and queued the email to the patient —
 * the frontend triggers no mail. A report is write-once: a second POST is 400.
 *
 * ITAM-2610: optional sections are trimmed and blank ones omitted from the
 * body, so the backend stores NULL rather than an empty string (the PDF
 * prints "/" for such sections).
 */
export const writeConsultReport = (
  consultId: number,
  payload: OnlineConsultReportPayload,
  signal?: AbortSignal
): Promise<OnlineConsultReportResponse> => {
  const body: OnlineConsultReportPayload = { report: payload.report.trim() };
  for (const field of OPTIONAL_REPORT_SECTIONS) {
    const value = payload[field]?.trim();
    if (value) {
      body[field] = value;
    }
  }
  return axios
    .post<OnlineConsultReportResponse>(
      `/online_consult/${consultId}/report`,
      body,
      { signal }
    )
    .then((res) => res.data);
};

/**
 * GET the stored report PDF as a Blob (for an <a download> object-URL click).
 * 404 means no report exists for the consult — the caller should treat the
 * row's hasReport as stale and refresh.
 */
export const fetchConsultReportPdf = (
  consultId: number,
  signal?: AbortSignal
): Promise<Blob> => {
  return axios
    .get<Blob>(`/online_consult/${consultId}/report/pdf`, {
      responseType: "blob",
      signal,
    })
    .then((res) => res.data);
};
